import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ChevronDown, ChevronUp, Search, Plus, ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { identitaFiscale } from '../lib/identitaFiscale';
import { searchCustomers, type CustomerCompact } from '../api/customers';
import { uploadFotoPreSubmit } from '../api/richieste';
import { suggestedEmail } from '../lib/emailTypo';
import { listProduttori, createProduttore } from '../api/produttori';
import type { Produttore } from '../types/produttore';
import apiClient from '../api/client';
import { useCategorie } from '../hooks/useCategorie';
import { useDebounce } from '../hooks/useDebounce';

// ── Zod schema ───────────────────────────────────────────────────────────────

const creaRichiestaSchema = z.object({
  // Categorie — valori data-driven: le opzioni provengono da settings_categorie_form
  // (caricate a runtime), quindi il valore è una stringa fra quelle proposte; la
  // validazione di esistenza/attività è garantita dalle opzioni mostrate + dal backend.
  categorie: z.array(z.string()).min(1, i18n.t('richieste.crea.val_categoria')),

  // Cliente
  ragione_sociale: z.string().min(1, i18n.t('richieste.crea.val_ragione_sociale')),
  piva: z.string().refine((v) => !v || /^\d{11}$/.test(v), { message: i18n.t('richieste.crea.val_piva') }).optional().or(z.literal('')),
  // 16 caratteri (persona fisica) o 11 cifre (ente senza P.IVA) — stessa regola
  // di `schemas/cliente.ts`; il checksum lo verifica il backend.
  codice_fiscale: z.string().refine((v) => !v || v.length === 16 || /^\d{11}$/.test(v), { message: i18n.t('richieste.crea.val_cf') }).optional().or(z.literal('')),
  sede_legale: z.string().min(1, i18n.t('richieste.crea.val_sede_legale')),
  referente: z.string().optional(),
  telefono: z.string().min(1, i18n.t('richieste.crea.val_telefono')),
  email: z.string().email(i18n.t('richieste.crea.val_email')),
  pec: z.string().email(i18n.t('richieste.crea.val_pec')).optional().or(z.literal('')),
  // R5/F2 — codice destinatario SDI opzionale (formato se fornito); nessun obbligo qui.
  codice_destinatario: z.string().refine((v) => !v || /^[A-Za-z0-9]{7}$/.test(v), { message: i18n.t('anagrafiche.val_cliente.codice_dest') }).optional().or(z.literal('')),

  // Origine
  origine_indirizzo: z.string().optional(),
  origine_comune: z.string().optional(),
  origine_provincia: z.string().refine((v) => !v || v.length === 2, { message: i18n.t('richieste.crea.val_provincia') }).optional().or(z.literal('')),
  origine_cap: z.string().refine((v) => !v || /^\d{5}$/.test(v), { message: i18n.t('richieste.crea.val_cap') }).optional().or(z.literal('')),

  // Produttore
  produttore_coincide: z.boolean(),
  produttore_id: z.string().optional(),

  // GSE (visibile solo se pv_pro incluso)
  conto_energia: z.enum(['si', 'no', 'non_so']).optional(),
  gse_codice_impianto: z.string().optional(),
  gse_censimp: z.string().optional(),
  gse_conto_energia: z.enum(['I', 'II', 'III', 'IV', 'V']).optional(),
  gse_potenza_kw: z.string().optional(),
  gse_data_esercizio: z.string().optional(),
  gse_causale: z.enum(['fine_vita', 'revamping', 'danno', 'smontaggio', 'altro']).optional(),
  gse_sogg_responsabile: z.boolean().optional(),

  // PV
  pv_n_pannelli: z.string().optional(),
  pv_marca: z.string().optional(),
  pv_modello: z.string().optional(),
  pv_smontati: z.boolean().optional(),
  pv_imballaggio: z.enum(['bancali', 'big_bags', 'sfusi', 'da_definire']).optional(),
  pv_vetri_rotti: z.boolean().optional(),

  // Serramenti
  serr_materiale: z.enum(['pvc', 'alluminio', 'misto']).optional(),
  serr_peso_kg: z.string().optional(),
  serr_n_infissi: z.string().optional(),

  // Cavi
  cavi_peso_kg: z.string().optional(),
  cavi_tipologia: z.enum(['rame_puro', 'rame_pvc', 'misto']).optional(),
  cavi_pct_rame: z.string().optional(),

  // Modalità
  modalita_conferimento: z.enum(['porto', 'ritiro', 'nd']),
  frequenza_conferimento: z.enum(['una_tantum', 'mensile', 'trimestrale', 'da_definire']).optional(),
  stato_materiale: z.enum(['integro', 'parzialmente_danneggiato', 'rotto']).optional(),
  processo_generazione: z.string().optional(),   // R2-E

  // Note
  note_cliente: z.string().optional(),

  // Conferme
  dichiarazione_accettata: z.boolean(),
  gdpr_consenso: z.boolean(),
}).refine((d) => !!(d.piva?.trim() || d.codice_fiscale?.trim()), {
  message: i18n.t('richieste.crea.val_piva_o_cf'),
  path: ['piva'],
});

type FormData = z.infer<typeof creaRichiestaSchema>;

// ── Helpers ───────────────────────────────────────────────────────────────────

const inputCls = 'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const selectCls = inputCls;

function SectionAccordion({
  title,
  open,
  onToggle,
  children,
  error,
}: {
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  error?: boolean;
}) {
  return (
    // No `overflow-hidden`: clippava il dropdown degli autocomplete annidati
    // (cliente/produttore), lasciando visibile un solo risultato. Gli angoli
    // arrotondati sono ora gestiti direttamente sull'header.
    <div className="bg-white border border-stone-mid rounded-xl">
      <button
        type="button"
        onClick={onToggle}
        className={`w-full flex items-center justify-between px-5 py-3.5 bg-stone-bg hover:bg-stone-mid transition-colors rounded-t-xl ${open ? '' : 'rounded-b-xl'} ${error ? 'border-b border-accent-danger' : 'border-b border-stone-mid'}`}
      >
        <span className={`text-sm font-semibold ${error ? 'text-accent-danger' : 'text-ink-dark'}`}>{title}</span>
        {open ? <ChevronUp size={16} className="text-ink-soft" /> : <ChevronDown size={16} className="text-ink-soft" />}
      </button>
      {open && <div className="px-5 py-4">{children}</div>}
    </div>
  );
}

function FieldWrap({
  label,
  error,
  required,
  children,
}: {
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs text-ink-soft mb-1">
        {label}
        {required && <span className="text-accent-danger ml-0.5">*</span>}
      </label>
      {children}
      {error && <p className="text-xs text-accent-danger mt-0.5">{error}</p>}
    </div>
  );
}

// ── Autocomplete cliente ──────────────────────────────────────────────────────

function ClienteAutocomplete({
  onSelect,
  onClear,
}: {
  onSelect: (c: CustomerCompact) => void;
  onClear: () => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debouncedQ = useDebounce(query, 300);
  const [suggestions, setSuggestions] = useState<CustomerCompact[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void (async () => {
      if (!debouncedQ.trim()) { setSuggestions([]); return; }
      setLoading(true);
      try {
        setSuggestions(await searchCustomers(debouncedQ, 10));
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [debouncedQ]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDrop(true); }}
          onFocus={() => query && setShowDrop(true)}
          placeholder={t('richieste.crea.cerca_cliente')}
          className={`${inputCls} pl-8`}
        />
      </div>
      {showDrop && query.trim() && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-stone-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-ink-soft">{t('richieste.crea.ricerca_in_corso')}</div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ink-faint">{t('richieste.crea.nessun_risultato')}</div>
          ) : (
            <ul>
              {suggestions.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => { onSelect(c); setQuery(c.ragione_sociale); setShowDrop(false); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-stone-bg transition-colors"
                  >
                    <div className="text-sm font-medium text-ink-dark">{c.ragione_sociale}</div>
                    <div className="text-xs text-ink-soft">
                      {identitaFiscale(c)}
                      {c.email ? ` · ${c.email}` : ''}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={() => { onClear(); setQuery(''); setSuggestions([]); }}
        className="mt-1 text-xs text-ink-soft hover:text-accent-danger transition-colors flex items-center gap-1"
      >
        <Plus size={11} />
        Nuovo cliente (svuota campi)
      </button>
    </div>
  );
}

// ── Autocomplete produttore ───────────────────────────────────────────────────

function ProduttoreAutocomplete({
  onSelect,
}: {
  onSelect: (p: Produttore) => void;
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const debouncedQ = useDebounce(query, 300);
  const [suggestions, setSuggestions] = useState<Produttore[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDrop, setShowDrop] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    void (async () => {
      if (!debouncedQ.trim()) { setSuggestions([]); return; }
      setLoading(true);
      try {
        const res = await listProduttori({ search: debouncedQ, limit: 10 });
        setSuggestions(res.items);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [debouncedQ]);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setShowDrop(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-faint pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setShowDrop(true); }}
          onFocus={() => query && setShowDrop(true)}
          placeholder={t('richieste.crea.cerca_produttore')}
          className={`${inputCls} pl-8`}
        />
      </div>
      {showDrop && query.trim() && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-stone-border rounded-lg shadow-lg max-h-72 overflow-y-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-ink-soft">{t('richieste.crea.ricerca_in_corso')}</div>
          ) : suggestions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-ink-faint">{t('richieste.crea.nessun_risultato')}</div>
          ) : (
            <ul>
              {suggestions.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => { onSelect(p); setQuery(p.ragione_sociale); setShowDrop(false); }}
                    className="w-full text-left px-3 py-2.5 hover:bg-stone-bg transition-colors"
                  >
                    <div className="text-sm font-medium text-ink-dark">{p.ragione_sociale}</div>
                    <div className="text-xs text-ink-soft font-mono">
                      {identitaFiscale(p)}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Pagina principale ─────────────────────────────────────────────────────────

export default function CreaRichiestaPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState,
  } = useForm<FormData>({
    resolver: zodResolver(creaRichiestaSchema),
    defaultValues: {
      categorie: [],
      modalita_conferimento: 'nd',
      produttore_coincide: true,
      dichiarazione_accettata: true,
      gdpr_consenso: true,
    },
  });

  const { errors, isSubmitting } = formState;

  const [submitError, setSubmitError] = useState<string | null>(null);

  // R6-C — suggerimento typo dominio email (non bloccante).
  const [emailHint, setEmailHint] = useState<string | null>(null);

  // R5/F6 — foto materiale caricate PRIMA del submit (max 5), incluse in foto_ids.
  const [fotoIds, setFotoIds] = useState<string[]>([]);
  const [fotoBusy, setFotoBusy] = useState(false);
  const [fotoError, setFotoError] = useState<string | null>(null);

  async function onFotoSelected(files: FileList | null) {
    if (!files || files.length === 0) return;
    setFotoError(null);
    const disponibili = 5 - fotoIds.length;
    if (disponibili <= 0) { setFotoError(t('richieste.foto.max_raggiunto', { n: 5 })); return; }
    setFotoBusy(true);
    try {
      for (const f of Array.from(files).slice(0, disponibili)) {
        // Pre-check dimensione client-side: non spedire file oltre 10 MB.
        if (f.size > 10 * 1024 * 1024) {
          setFotoError(t('richieste.foto.errore_size'));
          continue;
        }
        try {
          const pid = await uploadFotoPreSubmit(f);
          setFotoIds((prev) => [...prev, pid]);
        } catch (e: unknown) {
          const st = (e as { response?: { status?: number } })?.response?.status;
          setFotoError(st === 413 ? t('richieste.foto.errore_size')
            : st === 422 ? t('richieste.foto.errore_formato')
            : t('richieste.foto.errore_upload'));
        }
      }
    } finally {
      setFotoBusy(false);
    }
  }

  // Accordion state
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    categorie: true,
    cliente: true,
    origine: true,
    produttore: true,
    gse: true,
    dettagli: true,
    modalita: true,
    note: true,
    conferme: true,
  });

  const toggleSection = (name: string) => {
    setOpenSections((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Categorie data-driven da settings_categorie_form (caricate via store condiviso)
  const { categorie: categorieOptions, isLoading: categorieLoading, error: categorieError } = useCategorie();

  // Watch relevant fields
  // eslint-disable-next-line react-hooks/incompatible-library -- RHF watch(): il progetto non usa il React Compiler, comportamento invariato
  const categorie = watch('categorie') ?? [];
  const produttoreCoincide = watch('produttore_coincide');
  const hasPvPro = categorie.includes('pv_pro');
  const hasPV = categorie.includes('pv_pro') || categorie.includes('pv_dom');
  const hasSerr = categorie.includes('serramenti');
  const hasCavi = categorie.includes('cavi');

  // Produttore separato state
  const [selectedProduttore, setSelectedProduttore] = useState<Produttore | null>(null);
  const [showCreateProd, setShowCreateProd] = useState(false);
  const [creatingProd, setCreatingProd] = useState(false);
  const [createProdError, setCreateProdError] = useState<string | null>(null);
  const [newProdName, setNewProdName] = useState('');
  const [newProdPiva, setNewProdPiva] = useState('');
  const [newProdCf, setNewProdCf] = useState('');

  async function handleCreateProduttore() {
    if (!newProdName.trim()) return;
    setCreatingProd(true);
    setCreateProdError(null);
    try {
      const p = await createProduttore({
        ragione_sociale: newProdName.trim(),
        piva: newProdPiva.trim() || null,
        codice_fiscale: newProdCf.trim() || null,
      });
      setSelectedProduttore(p);
      setValue('produttore_id', p.id);
      setShowCreateProd(false);
      setNewProdName(''); setNewProdPiva(''); setNewProdCf('');
    } catch {
      setCreateProdError(t('richieste.crea.errore_crea_produttore'));
    } finally {
      setCreatingProd(false);
    }
  }

  // Submit
  const onSubmit = async (data: FormData) => {
    setSubmitError(null);
    try {
      const payload: Record<string, unknown> = {
        categorie: data.categorie,
        cliente: {
          ragione_sociale: data.ragione_sociale,
          piva: data.piva?.trim() || undefined,
          cf: data.codice_fiscale?.trim() || undefined,
          sede_legale: data.sede_legale,
          referente: data.referente?.trim() || undefined,
          telefono: data.telefono,
          email: data.email,
          pec: data.pec?.trim() || undefined,
          codice_destinatario: data.codice_destinatario?.trim() || undefined,
        },
        produttore_coincide: data.produttore_coincide,
        modalita_conferimento: data.modalita_conferimento,
        dichiarazione_accettata: data.dichiarazione_accettata,
        gdpr_consenso: data.gdpr_consenso,
        foto_ids: fotoIds,
      };

      // Origine
      if (data.origine_indirizzo || data.origine_comune || data.origine_provincia || data.origine_cap) {
        payload['origine'] = {
          indirizzo: data.origine_indirizzo || null,
          comune: data.origine_comune || null,
          provincia: data.origine_provincia || null,
          cap: data.origine_cap || null,
        };
      }

      // Produttore separato
      if (!data.produttore_coincide && selectedProduttore) {
        payload['produttore'] = {
          ragione_sociale: selectedProduttore.ragione_sociale,
          piva: selectedProduttore.piva || undefined,
          cf: selectedProduttore.codice_fiscale || undefined,
        };
      }

      // GSE
      if (hasPvPro && data.conto_energia) {
        payload['conto_energia'] = data.conto_energia;
        if (data.conto_energia === 'si') {
          payload['gse'] = {
            codice_impianto: data.gse_codice_impianto || undefined,
            censimp: data.gse_censimp || undefined,
            conto_energia: data.gse_conto_energia || undefined,
            potenza_kw: data.gse_potenza_kw ? parseFloat(data.gse_potenza_kw) : undefined,
            data_esercizio: data.gse_data_esercizio || undefined,
            causale: data.gse_causale || undefined,
            sogg_responsabile: data.gse_sogg_responsabile,
          };
        }
      }

      // PV
      if (hasPV) {
        payload['pv'] = {
          n_pannelli: data.pv_n_pannelli ? parseInt(data.pv_n_pannelli) : undefined,
          marca: data.pv_marca || undefined,
          modello: data.pv_modello || undefined,
          smontati: data.pv_smontati,
          imballaggio: data.pv_imballaggio || undefined,
          vetri_rotti: data.pv_vetri_rotti,
        };
      }

      // Serramenti
      if (hasSerr) {
        payload['serramenti'] = {
          materiale: data.serr_materiale || undefined,
          peso_kg: data.serr_peso_kg ? parseFloat(data.serr_peso_kg) : undefined,
          n_infissi: data.serr_n_infissi ? parseInt(data.serr_n_infissi) : undefined,
        };
      }

      // Cavi
      if (hasCavi) {
        payload['cavi'] = {
          peso_kg: data.cavi_peso_kg ? parseFloat(data.cavi_peso_kg) : undefined,
          tipologia: data.cavi_tipologia || undefined,
          pct_rame: data.cavi_pct_rame ? parseFloat(data.cavi_pct_rame) : undefined,
        };
      }

      // Frequenza/Stato
      if (data.frequenza_conferimento) payload['frequenza_conferimento'] = data.frequenza_conferimento;
      if (data.stato_materiale) payload['stato_materiale'] = data.stato_materiale;

      if (data.processo_generazione?.trim()) payload['processo_generazione'] = data.processo_generazione;   // R2-E

      if (data.note_cliente?.trim()) payload['note_cliente'] = data.note_cliente;

      const { data: resp } = await apiClient.post<{ order_ids: string[]; codici_ordine: string[] }>(
        '/api/richieste',
        payload,
      );

      const firstId = resp.order_ids[0];
      navigate(`/richieste/${firstId}`);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      if (status === 422) {
        setSubmitError(t('richieste.crea.errore_dati_non_validi'));
      } else {
        setSubmitError(t('richieste.crea.errore_creazione'));
      }
    }
  };

  return (
    <div className="p-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate('/richieste')}
            className="text-ink-soft hover:text-ink-dark transition-colors"
          >
            <ArrowLeft size={18} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-ink-dark tracking-tight">{t('richieste.lista.nuova_richiesta')}</h1>
            <p className="text-sm text-ink-soft mt-0.5">{t('richieste.crea.sottotitolo')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">

          {/* 1. Categorie */}
          <SectionAccordion
            title={t('richieste.crea.sez_categorie')}
            open={openSections['categorie']}
            onToggle={() => toggleSection('categorie')}
            error={!!errors.categorie}
          >
            <p className="text-xs text-ink-soft mb-3">{t('richieste.crea.seleziona_categoria')}</p>
            {categorieLoading ? (
              <p className="text-sm text-ink-soft">{t('richieste.crea.caricamento_categorie')}</p>
            ) : categorieError ? (
              <p className="text-sm text-accent-danger">{t('richieste.crea.errore_categorie')}</p>
            ) : categorieOptions.length === 0 ? (
              <p className="text-sm text-ink-soft">{t('richieste.crea.nessuna_categoria')}</p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {categorieOptions.map((opt) => (
                  <Controller
                    key={opt.codice}
                    control={control}
                    name="categorie"
                    render={({ field }) => {
                      const checked = field.value?.includes(opt.codice) ?? false;
                      return (
                        <label className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${checked ? 'border-sage bg-sage-light' : 'border-stone-border hover:bg-stone-bg'}`}>
                          <input
                            type="checkbox"
                            value={opt.codice}
                            checked={checked}
                            onChange={(e) => {
                              const newVal = e.target.checked
                                ? [...(field.value ?? []), opt.codice]
                                : (field.value ?? []).filter((v) => v !== opt.codice);
                              field.onChange(newVal);
                            }}
                            className="accent-sage"
                          />
                          <span className="text-sm font-medium text-ink-dark">{opt.nome_visualizzato}</span>
                        </label>
                      );
                    }}
                  />
                ))}
              </div>
            )}
            {errors.categorie && (
              <p className="text-xs text-accent-danger mt-2">{errors.categorie.message}</p>
            )}
          </SectionAccordion>

          {/* 2. Cliente */}
          <SectionAccordion
            title={t('richieste.crea.sez_cliente')}
            open={openSections['cliente']}
            onToggle={() => toggleSection('cliente')}
            error={!!(errors.ragione_sociale || errors.piva || errors.email || errors.telefono)}
          >
            <div className="mb-4">
              <ClienteAutocomplete
                onSelect={(c) => {
                  setValue('ragione_sociale', c.ragione_sociale);
                  setValue('piva', c.piva ?? '');
                  setValue('codice_fiscale', c.codice_fiscale ?? '');
                  setValue('sede_legale', c.sede_legale ?? '');
                  setValue('referente', c.referente ?? '');
                  setValue('telefono', c.telefono ?? '');
                  setValue('email', c.email ?? '');
                  setValue('pec', c.pec ?? '');
                }}
                onClear={() => {
                  setValue('ragione_sociale', '');
                  setValue('piva', '');
                  setValue('codice_fiscale', '');
                  setValue('email', '');
                  setValue('telefono', '');
                  setValue('sede_legale', '');
                  setValue('referente', '');
                  setValue('pec', '');
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} error={errors.ragione_sociale?.message} required>
                  <input className={inputCls} {...register('ragione_sociale')} />
                </FieldWrap>
              </div>
              <FieldWrap label={t('anagrafiche.campi.partita_iva')} error={errors.piva?.message}>
                <input className={inputCls} {...register('piva')} maxLength={11} placeholder={t('richieste.crea.ph_piva')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.codice_fiscale')} error={errors.codice_fiscale?.message}>
                <input className={inputCls} {...register('codice_fiscale')} maxLength={16} />
              </FieldWrap>
              <div className="col-span-2">
                <FieldWrap label={t('anagrafiche.campi.sede_legale')} error={errors.sede_legale?.message} required>
                  <input className={inputCls} {...register('sede_legale')} placeholder={t('richieste.crea.ph_sede_legale')} />
                </FieldWrap>
              </div>
              <FieldWrap label={t('anagrafiche.campi.referente')}>
                <input className={inputCls} {...register('referente')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.telefono')} error={errors.telefono?.message} required>
                <input className={inputCls} {...register('telefono')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.email')} error={errors.email?.message} required>
                {(() => {
                  const emailReg = register('email');
                  return (
                    <input className={inputCls} type="email" {...emailReg}
                      onBlur={(e) => { emailReg.onBlur(e); setEmailHint(suggestedEmail(e.target.value.trim())); }}
                      onChange={(e) => { emailReg.onChange(e); if (emailHint) setEmailHint(null); }} />
                  );
                })()}
                {emailHint && (
                  <p className="text-xs text-ink-soft mt-0.5" data-testid="email-typo-hint">
                    {t('richieste.crea.email_forse')}{' '}
                    <button type="button" data-testid="email-typo-apply"
                      onClick={() => { setValue('email', emailHint); setEmailHint(null); }}
                      className="text-sage-dark underline hover:text-sage">{emailHint}</button>?
                  </p>
                )}
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.pec')} error={errors.pec?.message}>
                <input className={inputCls} type="email" {...register('pec')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.codice_destinatario')} error={errors.codice_destinatario?.message}>
                <input className={inputCls} {...register('codice_destinatario')} maxLength={7} placeholder={t('anagrafiche.crea_cliente.ph_codice_dest')} />
              </FieldWrap>
            </div>
          </SectionAccordion>

          {/* 3. Origine */}
          <SectionAccordion
            title={t('richieste.crea.sez_origine')}
            open={openSections['origine']}
            onToggle={() => toggleSection('origine')}
          >
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <FieldWrap label={t('anagrafiche.campi.indirizzo')}>
                  <input className={inputCls} {...register('origine_indirizzo')} placeholder={t('richieste.crea.ph_via')} />
                </FieldWrap>
              </div>
              <FieldWrap label={t('anagrafiche.campi.comune')}>
                <input className={inputCls} {...register('origine_comune')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.provincia')} error={errors.origine_provincia?.message}>
                <input className={inputCls} {...register('origine_provincia')} maxLength={2} placeholder={t('richieste.crea.ph_provincia')} />
              </FieldWrap>
              <FieldWrap label={t('anagrafiche.campi.cap')} error={errors.origine_cap?.message}>
                <input className={inputCls} {...register('origine_cap')} maxLength={5} placeholder={t('richieste.crea.ph_cap')} />
              </FieldWrap>
            </div>
          </SectionAccordion>

          {/* 4. Produttore */}
          <SectionAccordion
            title={t('richieste.crea.sez_produttore')}
            open={openSections['produttore']}
            onToggle={() => toggleSection('produttore')}
          >
            <div className="space-y-3">
              <Controller
                control={control}
                name="produttore_coincide"
                render={({ field }) => (
                  <div className="flex gap-3">
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm font-medium flex-1 ${field.value ? 'border-sage bg-sage-light text-sage-dark' : 'border-stone-border text-ink-mid hover:bg-stone-bg'}`}>
                      <input
                        type="radio"
                        checked={field.value === true}
                        onChange={() => field.onChange(true)}
                        className="accent-sage"
                      />
                      Produttore = Cliente
                    </label>
                    <label className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border cursor-pointer transition-colors text-sm font-medium flex-1 ${!field.value ? 'border-sage bg-sage-light text-sage-dark' : 'border-stone-border text-ink-mid hover:bg-stone-bg'}`}>
                      <input
                        type="radio"
                        checked={field.value === false}
                        onChange={() => field.onChange(false)}
                        className="accent-sage"
                      />
                      Produttore diverso
                    </label>
                  </div>
                )}
              />

              {!produttoreCoincide && (
                <div className="space-y-3 pt-2">
                  {selectedProduttore ? (
                    <div className="flex items-center justify-between p-3 bg-stone-bg border border-stone-mid rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-ink-dark">{selectedProduttore.ragione_sociale}</p>
                        {selectedProduttore.piva && <p className="text-xs text-ink-soft font-mono">P.IVA {selectedProduttore.piva}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => { setSelectedProduttore(null); setValue('produttore_id', undefined); }}
                        className="text-xs text-accent-danger hover:underline"
                      >
                        Rimuovi
                      </button>
                    </div>
                  ) : (
                    <ProduttoreAutocomplete
                      onSelect={(p) => {
                        setSelectedProduttore(p);
                        setValue('produttore_id', p.id);
                      }}
                    />
                  )}

                  {/* Crea nuovo produttore */}
                  <div className="border border-stone-mid rounded-lg overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setShowCreateProd((v) => !v)}
                      className="w-full flex items-center justify-between px-4 py-2.5 bg-stone-bg text-sm text-ink-mid hover:bg-stone-mid transition-colors"
                    >
                      <span className="flex items-center gap-2"><Plus size={13} />{t('richieste.crea.crea_nuovo_produttore')}</span>
                      {showCreateProd ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                    </button>
                    {showCreateProd && (
                      <div className="px-4 py-3 space-y-3">
                        {createProdError && <p className="text-xs text-accent-danger">{createProdError}</p>}
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <FieldWrap label={t('anagrafiche.campi.ragione_sociale')} required>
                              <input className={inputCls} value={newProdName} onChange={(e) => setNewProdName(e.target.value)} />
                            </FieldWrap>
                          </div>
                          <FieldWrap label={t('anagrafiche.campi.partita_iva')}>
                            <input className={inputCls} value={newProdPiva} onChange={(e) => setNewProdPiva(e.target.value)} maxLength={11} />
                          </FieldWrap>
                          <FieldWrap label={t('anagrafiche.campi.codice_fiscale')}>
                            <input className={inputCls} value={newProdCf} onChange={(e) => setNewProdCf(e.target.value)} maxLength={16} />
                          </FieldWrap>
                        </div>
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={handleCreateProduttore}
                            disabled={creatingProd}
                            className="px-4 py-1.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60"
                          >
                            {creatingProd ? t('richieste.crea.creazione_in_corso') : t('richieste.crea.crea_e_seleziona')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </SectionAccordion>

          {/* 5. GSE (solo se pv_pro) */}
          {hasPvPro && (
            <SectionAccordion
              title={t('richieste.crea.sez_gse')}
              open={openSections['gse']}
              onToggle={() => toggleSection('gse')}
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <FieldWrap label={t('richieste.campi.conto_energia')}>
                    <Controller
                      control={control}
                      name="conto_energia"
                      render={({ field }) => (
                        <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="si">{t('common.si')}</option>
                          <option value="no">{t('common.no')}</option>
                          <option value="non_so">{t('richieste.campi.conto_energia_non_so')}</option>
                        </select>
                      )}
                    />
                  </FieldWrap>
                </div>
                <FieldWrap label={t('richieste.campi.gse_codice_impianto')}>
                  <input className={inputCls} {...register('gse_codice_impianto')} />
                </FieldWrap>
                <FieldWrap label={t('richieste.campi.gse_censimp')}>
                  <input className={inputCls} {...register('gse_censimp')} />
                </FieldWrap>
                <FieldWrap label={t('richieste.crea.gse_conto_energia_nro')}>
                  <Controller
                    control={control}
                    name="gse_conto_energia"
                    render={({ field }) => (
                      <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                        <option value="">{t('common.seleziona')}</option>
                        {['I', 'II', 'III', 'IV', 'V'].map((v) => <option key={v} value={v}>{v}° Conto Energia</option>)}
                      </select>
                    )}
                  />
                </FieldWrap>
                <FieldWrap label={t('richieste.campi.gse_potenza')}>
                  <input className={inputCls} type="number" min={0} step="0.01" {...register('gse_potenza_kw')} />
                </FieldWrap>
                <FieldWrap label={t('richieste.crea.gse_data_esercizio')}>
                  <input className={inputCls} type="date" {...register('gse_data_esercizio')} />
                </FieldWrap>
                <FieldWrap label={t('richieste.campi.gse_causale')}>
                  <Controller
                    control={control}
                    name="gse_causale"
                    render={({ field }) => (
                      <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                        <option value="">{t('common.seleziona')}</option>
                        <option value="fine_vita">{t('richieste.campi.causale_fine_vita')}</option>
                        <option value="revamping">{t('richieste.campi.causale_revamping')}</option>
                        <option value="danno">{t('richieste.campi.causale_danno')}</option>
                        <option value="smontaggio">{t('richieste.campi.causale_smontaggio')}</option>
                        <option value="altro">{t('richieste.campi.causale_altro')}</option>
                      </select>
                    )}
                  />
                </FieldWrap>
              </div>
            </SectionAccordion>
          )}

          {/* 6. Dettagli categoria */}
          {(hasPV || hasSerr || hasCavi) && (
            <SectionAccordion
              title={t('richieste.crea.sez_dettagli')}
              open={openSections['dettagli']}
              onToggle={() => toggleSection('dettagli')}
            >
              <div className="space-y-4">
                {hasPV && (
                  <div>
                    <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-3">{t('richieste.crea.sub_pv')}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <FieldWrap label={t('richieste.campi.n_pannelli')}>
                        <input className={inputCls} type="number" min={0} {...register('pv_n_pannelli')} />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.marca')}>
                        <input className={inputCls} {...register('pv_marca')} />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.modello')}>
                        <input className={inputCls} {...register('pv_modello')} />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.imballaggio')}>
                        <Controller
                          control={control}
                          name="pv_imballaggio"
                          render={({ field }) => (
                            <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                              <option value="">{t('common.seleziona')}</option>
                              <option value="bancali">{t('richieste.campi.imb_bancali')}</option>
                              <option value="big_bags">{t('richieste.campi.imb_big_bags')}</option>
                              <option value="sfusi">{t('richieste.campi.imb_sfusi')}</option>
                              <option value="da_definire">{t('richieste.campi.freq_da_definire')}</option>
                            </select>
                          )}
                        />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.gia_smontati')}>
                        <select className={selectCls} onChange={(e) => setValue('pv_smontati', e.target.value === 'true')}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="true">{t('common.si')}</option>
                          <option value="false">{t('common.no')}</option>
                        </select>
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.vetri_rotti')}>
                        <select className={selectCls} onChange={(e) => setValue('pv_vetri_rotti', e.target.value === 'true')}>
                          <option value="">{t('common.seleziona')}</option>
                          <option value="true">{t('common.si')}</option>
                          <option value="false">{t('common.no')}</option>
                        </select>
                      </FieldWrap>
                    </div>
                  </div>
                )}
                {hasSerr && (
                  <div>
                    <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-3">{t('richieste.crea.sub_serramenti')}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <FieldWrap label={t('richieste.campi.materiale')}>
                        <Controller
                          control={control}
                          name="serr_materiale"
                          render={({ field }) => (
                            <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                              <option value="">{t('common.seleziona')}</option>
                              <option value="pvc">PVC</option>
                              <option value="alluminio">{t('richieste.campi.mat_alluminio')}</option>
                              <option value="misto">{t('richieste.campi.mat_misto')}</option>
                            </select>
                          )}
                        />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.peso_stimato')}>
                        <input className={inputCls} type="number" min={0} step="0.01" {...register('serr_peso_kg')} />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.n_infissi')}>
                        <input className={inputCls} type="number" min={0} {...register('serr_n_infissi')} />
                      </FieldWrap>
                    </div>
                  </div>
                )}
                {hasCavi && (
                  <div>
                    <p className="text-xs font-semibold text-ink-soft uppercase tracking-wide mb-3">{t('richieste.crea.sub_cavi')}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <FieldWrap label={t('richieste.campi.peso_stimato')}>
                        <input className={inputCls} type="number" min={0} step="0.01" {...register('cavi_peso_kg')} />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.tipologia')}>
                        <Controller
                          control={control}
                          name="cavi_tipologia"
                          render={({ field }) => (
                            <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                              <option value="">{t('common.seleziona')}</option>
                              <option value="rame_puro">{t('richieste.campi.cavi_rame_puro')}</option>
                              <option value="rame_pvc">{t('richieste.campi.cavi_rame_pvc')}</option>
                              <option value="misto">{t('richieste.campi.mat_misto')}</option>
                            </select>
                          )}
                        />
                      </FieldWrap>
                      <FieldWrap label={t('richieste.campi.pct_rame')}>
                        <input className={inputCls} type="number" min={0} max={100} step="0.1" {...register('cavi_pct_rame')} />
                      </FieldWrap>
                    </div>
                  </div>
                )}
              </div>
            </SectionAccordion>
          )}

          {/* 7. Modalità conferimento */}
          <SectionAccordion
            title={t('richieste.crea.sez_modalita')}
            open={openSections['modalita']}
            onToggle={() => toggleSection('modalita')}
            error={!!errors.modalita_conferimento}
          >
            <div className="grid grid-cols-2 gap-4">
              <FieldWrap label={t('richieste.crea.modalita_label')} error={errors.modalita_conferimento?.message} required>
                <select className={selectCls} {...register('modalita_conferimento')}>
                  <option value="nd">{t('richieste.campi.modalita_nd')}</option>
                  <option value="porto">{t('richieste.campi.modalita_porto')}</option>
                  <option value="ritiro">{t('richieste.campi.modalita_ritiro')}</option>
                </select>
              </FieldWrap>
              <FieldWrap label={t('richieste.campi.frequenza')}>
                <Controller
                  control={control}
                  name="frequenza_conferimento"
                  render={({ field }) => (
                    <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                      <option value="">{t('common.seleziona')}</option>
                      <option value="una_tantum">{t('richieste.campi.freq_una_tantum')}</option>
                      <option value="mensile">{t('richieste.campi.freq_mensile')}</option>
                      <option value="trimestrale">{t('richieste.campi.freq_trimestrale')}</option>
                      <option value="da_definire">{t('richieste.campi.freq_da_definire')}</option>
                    </select>
                  )}
                />
              </FieldWrap>
              <FieldWrap label={t('richieste.campi.stato_materiale')}>
                <Controller
                  control={control}
                  name="stato_materiale"
                  render={({ field }) => (
                    <select className={selectCls} value={field.value ?? ''} onChange={(e) => field.onChange(e.target.value || undefined)}>
                      <option value="">{t('common.seleziona')}</option>
                      <option value="integro">{t('richieste.campi.stato_integro')}</option>
                      <option value="parzialmente_danneggiato">{t('richieste.campi.stato_parz_danneggiato')}</option>
                      <option value="rotto">{t('richieste.campi.stato_rotto')}</option>
                    </select>
                  )}
                />
              </FieldWrap>
              <FieldWrap label={t('richieste.campi.processo_generazione')}>
                <textarea className={inputCls} rows={2} data-testid="input-processo-generazione"
                  {...register('processo_generazione')} />
              </FieldWrap>
            </div>
          </SectionAccordion>

          {/* 8. Note */}
          <SectionAccordion
            title={t('richieste.crea.sez_note')}
            open={openSections['note']}
            onToggle={() => toggleSection('note')}
          >
            <FieldWrap label={t('richieste.crea.note_aggiuntive')}>
              <textarea className={inputCls} rows={4} {...register('note_cliente')} placeholder={t('richieste.crea.ph_note')} />
            </FieldWrap>
            <FieldWrap label={t('richieste.foto.titolo')}>
              <input type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple
                disabled={fotoBusy || fotoIds.length >= 5} data-testid="crea-foto-input"
                onChange={(e) => void onFotoSelected(e.target.files)} />
              <p className="text-xs text-ink-faint mt-1" data-testid="crea-foto-count">
                {t('richieste.foto.caricate', { n: fotoIds.length, max: 5 })}
              </p>
              {fotoError && <p className="text-xs text-accent-danger mt-1" data-testid="crea-foto-error">{fotoError}</p>}
            </FieldWrap>
          </SectionAccordion>

          {/* 10. Conferme */}
          <SectionAccordion
            title={t('richieste.crea.sez_conferme')}
            open={openSections['conferme']}
            onToggle={() => toggleSection('conferme')}
            error={!!(errors.dichiarazione_accettata || errors.gdpr_consenso)}
          >
            <div className="space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" {...register('dichiarazione_accettata')} className="mt-0.5 accent-sage" />
                <span className="text-sm text-ink-dark">
                  {t('richieste.crea.dichiarazione')}
                </span>
              </label>
              {errors.dichiarazione_accettata && (
                <p className="text-xs text-accent-danger">{errors.dichiarazione_accettata.message}</p>
              )}
              <label className="flex items-start gap-3 cursor-pointer">
                <input type="checkbox" {...register('gdpr_consenso')} className="mt-0.5 accent-sage" />
                <span className="text-sm text-ink-dark">
                  {t('richieste.crea.gdpr')}
                </span>
              </label>
              {errors.gdpr_consenso && (
                <p className="text-xs text-accent-danger">{errors.gdpr_consenso.message}</p>
              )}
            </div>
          </SectionAccordion>

          {/* Submit */}
          {submitError && (
            <div className="px-4 py-3 bg-accent-danger-bg border border-[#FECACA] rounded-xl text-sm text-accent-danger">
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate('/richieste')}
              className="px-5 py-2.5 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-stone-bg transition-colors"
            >
              {t('common.azioni.annulla')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2.5 text-sm font-medium text-white bg-sage rounded-lg hover:bg-[#5C6F59] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? t('richieste.crea.creazione_in_corso') : t('richieste.crea.crea_richiesta')}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
}
